// Drzewo kont hero D „schemat metra” + kamera sterowana przewijaniem (paczka HD, runda 41-D-R2).
//
// Port 1:1 generatora `design/claude-design-wyniki/41-minimal-strona-glowna/D-R2/src/41dr2-drzewo.js`
// BEZ DOM: `buildWorld(orient)` liczy konta, ścieżki, przystanki, bbox, `sMin`, progi pojawiania się
// i nicki; `camera(world, p)` to ta sama funkcja kamery. Ziarno LCG (4101) i stałe (CFG, NPTS, LOD,
// KADRY, NICKS) są przeniesione bez zmian; jedyna zmiana nazw to orientacje: generator mówi `m` / `d`
// (ramka telefonu / komputera), tutaj `v` (pion, czas w dół) i `h` (poziom, czas w prawo).
//
// Świat NIE jest liczony w przeglądarce (pomiar koordynatora 23.09: 26 ms / 18 ms w Node, czyli rząd
// 70-100 ms na telefonie przy CPU × 4). `frontend/scripts/gen-landing-tree.mjs` ładuje ten plik wprost
// w Node (typy zdejmuje Node 24), więc tu nie ma enumów, przestrzeni nazw ani importów względnych,
// i zapisuje `landingTree.data.ts`; test pilnuje, że plik danych = wynik `buildWorld`.
//
// Współrzędne przepływu: a = wzdłuż (czas), b = w poprzek (konta). Świat = piksele mapy D
// (pion: 358 × 300 od lewego górnego rogu mapy; poziom: 720 × 520). Trzy konta D stoją w miejscu.

export type Orient = 'v' | 'h'
export type PlatformKey = 'tt' | 'ig' | 'yt'

/** Jedno konto świata (pozycja w indeksie = kolejność w poprzek). */
export interface TreeAccount {
  i: number
  /** Barwa encji w stałej kolejności accent / pink / orange (reguła 52): i % 3 + 1. */
  c: 1 | 2 | 3
  /** Jedno z trzech kont D (stoją w miejscu, ich węzły i trzy przystanki żyją w DOM). */
  d: boolean
  pf: PlatformKey
  kadr: string
  /** p, od którego konto jest widoczne; konta D: -1. */
  rev: number
  /** Nick pod awatarem albo '' (tylko tam, gdzie mieści się bez kolizji z sąsiadem). */
  nick: string
  /** Środek węzła konta w pikselach świata. */
  x: number
  y: number
  /** Linia konta; dla kont D sam ogon za krawędzią okna p = 0. */
  line: string
  /** Głowa linii konta D (rysowana ruchem wejścia, `pathLength="1"`); innym ''. */
  head: string
  /** Przystanki jako odcinki zerowej długości (`M x y h0`): podkład, wyszłe, zaplanowane. */
  bg: string
  ok: string
  sch: string
  /** Pierścienie „czeka na zgodę”. */
  wt: [number, number][]
  /** Węzeł konta jako kropka przy LOD 3. */
  wn: string
}

export interface World {
  orient: Orient
  /** Okno mapy (px ekranu), przesunięcie warstwy kamery w oknie i margines świata przy p = 1. */
  win: [number, number]
  off: [number, number]
  pad: [number, number]
  /** Promień awatara konta (połowa boku): 18 w pionie, 22 w poziomie. */
  nodeR: number
  /** bbox świata [x0, y0, x1, y1] z marginesem 8 px. */
  bb: [number, number, number, number]
  sMin: number
  /** „dziś” z D, jego przedłużenia przez cały świat i pień utwór → seria. */
  today: string
  todayX: string
  trunk: string
  accounts: TreeAccount[]
}

export interface Camera { s: number; tx: number; ty: number; css: string }

// progi p: l1 podpisy przystanków, l2 nicki i awatary, l3 loga
export const LOD = [0.08, 0.4, 0.64]
// ile kont widać przy p (docelowo ~10 / ~30 / ~100)
export const NPTS: [number, number][] = [[0, 3], [0.02, 3], [0.25, 10], [0.6, 30], [0.92, 100]]
/** Liczba klatek kamery na ścieżce WAAPI: camera(i / 20), i = 0..20. */
export const KEYFRAME_STEPS = 21

type Cfg = {
  S: number; B: number; turn: number; c1: number; c2: number; node: number; vis: number; step: number
  sF: number; sM: number; Y0: number; Yout: number; gam: number; end: number; lean: number
  win: [number, number]; off: [number, number]; pad: [number, number]; nodeR: number
  innerA: [number, number]; xy: (b: number, a: number) => [number, number]
}

// konfiguracje orientacji (liczby z MAPY D, pomiar z renderu 41-D)
export const CFG: Record<Orient, Cfg> = {
  v: { S: 102, B: 113, turn: 142, c1: 124, c2: 118, node: 160, vis: 300, step: 40, sF: 150, sM: 486, Y0: 100, Yout: 560,
    gam: 1.45, end: 990, lean: 0.30, win: [390, 300], off: [16, 0], pad: [16, 6], nodeR: 18,
    innerA: [150, 150], xy: (b, a) => [179 + b, a] },
  h: { S: 404, B: 172, turn: 444, c1: 430, c2: 418, node: 466, vis: 716, step: 84, sF: 228, sM: 640, Y0: 100, Yout: 740,
    gam: 1.45, end: 2140, lean: 0.30, win: [720, 520], off: [0, 0], pad: [8, 10], nodeR: 22,
    innerA: [260, 300], xy: (b, a) => [a, 268 + b] },
}

export const KADRY = ['n2t1', 'n5t1', 'hf1', 'n3t1', 'n1t3', 'n5t3', 'n4t3', 'n2t3']
// własne konta artysty (dane przykładowe jak w D; bez pseudonimów z imienia i nazwiska właściciela)
export const NICKS = ['@nocna.zmiana', '@kadry.nocne', '@nocna.jazda.live', '@mokry.chodnik', '@zostan.chwile.tv', '@przystanek.we.mgle',
  '@latarnia.i.deszcz', '@ulica.o.zmierzchu', '@jazda.nocna', '@nocne.kadry', '@chwile.nocne', 'Nocna jazda live',
  '@zmierzch.na.ulicy', '@przystanek.nocny', '@deszcz.i.latarnia', '@nocna.jazda.pl']

function lcg(seed: number): () => number {
  let s = seed >>> 0
  return () => { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 4294967296 }
}
const clamp = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v)
const lerp = (a: number, b: number, t: number) => a + (b - a) * t
const r1 = (v: number) => Math.round(v * 10) / 10

function pOfN(n: number): number {                    // odwrotność łamanej NPTS: przy jakim p widać n kont
  for (let i = 1; i < NPTS.length; i++) {
    const a = NPTS[i - 1], b = NPTS[i]
    if (n <= b[1] && b[1] > a[1]) return lerp(a[0], b[0], (n - a[1]) / (b[1] - a[1]))
  }
  return 1
}
const ease = (p: number) => 1 - Math.pow(1 - p, 1.6)  // u(p): szybciej na starcie, łagodne dojście do s_min
export function lodOf(p: number): 0 | 1 | 2 | 3 { return p >= LOD[2] ? 3 : p >= LOD[1] ? 2 : p >= LOD[0] ? 1 : 0 }

/* ── konta: 36 z lewej + konto D + 12 w luce + konto D + 12 w luce + konto D + 37 z prawej = 100 ── */
type Acc = {
  t: 'side' | 'near' | 'inner'; k?: number; sg?: number; pf?: PlatformKey; i: number; kadr: string
  rhythm: number; endJ: number; nx: number; ny: number; rev: number; nick: boolean
}
function accounts(): Acc[] {
  const rnd = lcg(4101)
  const out: Partial<Acc>[] = []
  for (let i = 35; i >= 0; i--) out.push({ t: 'side', k: i, sg: -1 })
  out.push({ t: 'near', k: 0, pf: 'tt' })
  for (let i = 1; i <= 12; i++) out.push({ t: 'inner' })
  out.push({ t: 'near', k: 1, pf: 'ig' })
  for (let i = 1; i <= 12; i++) out.push({ t: 'inner' })
  out.push({ t: 'near', k: 2, pf: 'yt' })
  for (let i = 0; i <= 36; i++) out.push({ t: 'side', k: i, sg: 1 })
  const pfs: PlatformKey[] = ['tt', 'ig', 'yt']
  out.forEach((a, idx) => {
    a.i = idx
    if (!a.pf) a.pf = pfs[Math.floor(rnd() * 3)]
    a.kadr = KADRY[Math.floor(rnd() * KADRY.length)]
    a.rhythm = [1, 1, 1.25, 1.5, 2][Math.floor(rnd() * 5)]
    a.endJ = rnd()
  })
  return out as Acc[]
}

/* ── geometria jednego konta: segmenty w (b, a) + przystanki + węzeł ── */
type Seg = [string, ...number[]]
type Geom = {
  segs: Seg[]; stops: [number, number, 'ok' | 'sch' | 'wt'][]; node: number; tail: [number, number, number, number]
  head: number; b: number; a0: number; aEnd: number; nodeB: number
}
function bAt(g: Geom, a: number): number {            // położenie w poprzek na linii przy danym a
  if (a <= g.a0) return g.b
  const t = (a - g.tail[1]) / (g.tail[3] - g.tail[1])
  return g.tail[0] + t * t * (g.tail[2] - g.tail[0])
}
function geom(acc: Acc, o: Cfg): Geom {
  const g = { segs: [], stops: [] } as unknown as Geom
  const S = o.S, near = acc.t === 'near', tailLen = o.end - o.vis
  let b: number, aHead: number
  if (near) {
    b = [-o.B, 0, o.B][acc.k as number]
    g.segs.push(['M', 0, S])
    if (b === 0) g.segs.push(['L', 0, o.turn]); else g.segs.push(['C', 0, o.c1, b, o.c2, b, o.turn])
    aHead = o.turn; g.node = o.node
  } else if (acc.t === 'inner') {
    // wewnątrz luki między kontami D: ten sam kształt co linia D, przeskalowany w poprzek
    const gapIdx = acc.i < 49 ? -1 : 1
    const j = gapIdx < 0 ? acc.i - 36 : acc.i - 49
    b = gapIdx < 0 ? -o.B + o.B * j / 13 : o.B * j / 13
    g.segs.push(['M', 0, S], ['C', 0, o.c1, b, o.c2, b, o.turn])
    aHead = o.turn
    g.node = o.turn + o.innerA[0] + ((j * 37) % 5) / 4 * o.innerA[1]
  } else {
    // po bokach: ćwiartka elipsy z punktu wyjścia serii; zewnętrzne łuki obejmują wewnętrzne
    const n = (acc.sg as number) < 0 ? 36 : 37, f = (acc.k as number) / (n - 1)
    const bb = o.sF + (o.sM - o.sF) * f
    b = (acc.sg as number) * bb
    const Y = o.Y0 + (o.Yout - o.Y0) * Math.pow(f, o.gam), K = 0.5523
    g.segs.push(['M', 0, S], ['C', K * b, S, b, S + Y * (1 - K), b, S + Y])
    aHead = S + Y; g.node = aHead + (o.node - o.turn)
  }
  const a0 = Math.max(aHead, o.vis)
  if (a0 > aHead) g.segs.push(['L', b, a0])
  let aEnd = near ? o.end : o.end - acc.endJ * 0.14 * tailLen
  if (aEnd < g.node + 3 * o.step) aEnd = g.node + 3 * o.step
  const bEnd = b * (1 + o.lean * (aEnd - a0) / tailLen)
  g.tail = [b, a0, bEnd, aEnd]
  g.segs.push(['Q', b, (a0 + aEnd) / 2, bEnd, aEnd])
  g.head = aHead; g.b = b; g.a0 = a0; g.aEnd = aEnd
  // przystanki: rytm konta; D ma własne 3 przystanki w DOM, świat dokłada ciąg dalszy za krawędzią okna p = 0
  let first = near ? [290, 296, 288][acc.k as number] : g.node + 1.3 * o.step
  if (near) first = o === CFG.h ? [700, 704, 704][acc.k as number] : first
  const st = o.step * (near ? 1 : acc.rhythm)
  let aS = first + (near ? st : 0), idx = 0
  const rnd = lcg(900 + acc.i)
  while (aS < aEnd - 0.25 * o.step) {
    let state: 'ok' | 'sch' | 'wt' = 'sch'
    if (!near) {
      if (idx === 0 && acc.i % 4 !== 3) state = 'ok'
      else if (idx === 1 && acc.i % 11 === 5) state = 'wt'
      else if (idx === 1 && acc.i % 3 === 0) state = 'ok'
    }
    g.stops.push([bAt(g, aS), aS, state])
    aS += st * (0.88 + 0.24 * rnd()); idx++
  }
  g.nodeB = bAt(g, g.node)
  return g
}

function pathD(segs: Seg[], o: Cfg): string {
  let s = ''
  for (const sg of segs) {
    const pts: string[] = []
    for (let i = 1; i < sg.length; i += 2) { const q = o.xy(sg[i] as number, sg[i + 1] as number); pts.push(r1(q[0]) + ' ' + r1(q[1])) }
    s += sg[0] + pts.join(' ')
  }
  return s
}
function dots(list: [number, number, ...unknown[]][], o: Cfg): string {
  return list.map((d) => { const q = o.xy(d[0], d[1]); return 'M' + r1(q[0]) + ' ' + r1(q[1]) + 'h0' }).join('')
}

/* ── kamera: skala rośnie geometrycznie (stała prędkość oddalania), zaczep = utwór u góry (v) / po lewej (h) ── */
export function camera(w: Pick<World, 'orient' | 'off' | 'pad' | 'win' | 'bb' | 'sMin'>, p: number): Camera {
  const s = Math.pow(w.sMin, ease(clamp(p, 0, 1))), e = (1 - s) / (1 - w.sMin), bb = w.bb
  let T: [number, number]
  if (w.orient === 'v') {
    const cx = (bb[0] + bb[2]) / 2, wx = lerp(179, cx, e), top = lerp(0, w.pad[1] - bb[1] * w.sMin, e)
    T = [195 - w.off[0] - s * wx, top]
  } else {
    const cy = (bb[1] + bb[3]) / 2, wy = lerp(268, cy, e), left = lerp(0, w.pad[0] - bb[0] * w.sMin, e)
    T = [left, lerp(268, w.win[1] / 2, e) - s * wy]
  }
  return { s, tx: T[0], ty: T[1], css: 'translate3d(' + r1(T[0]) + 'px,' + r1(T[1]) + 'px,0) scale(' + Math.round(s * 10000) / 10000 + ')' }
}

/** Klatki kamery dla ścieżki WAAPI: 21 × camera(i / 20). Liniowo między nimi (odchyłka w MAPIE D-R2). */
export function keyframes(w: World): { offset: number; transform: string }[] {
  return Array.from({ length: KEYFRAME_STEPS }, (_, i) => ({ offset: i / (KEYFRAME_STEPS - 1), transform: camera(w, i / (KEYFRAME_STEPS - 1)).css }))
}

/** Ile kont widać przy p: trzy konta D zawsze, reszta od swojego progu. */
export function visibleCount(w: World, p: number): number {
  let n = 0
  for (const a of w.accounts) if (a.d || p >= a.rev) n++
  return n
}

type RawWorld = { o: Cfg; orient: Orient; accs: Acc[]; bb: [number, number, number, number]; sMin: number }

function inWin(w: RawWorld, p: number, x: number, y: number, m: number | [number, number, number]): [number, number] | null {
  const c = camera({ orient: w.orient, off: w.o.off, pad: w.o.pad, win: w.o.win, bb: w.bb, sMin: w.sMin }, p)
  const o = w.o, M = typeof m === 'number' ? [m, m, m] : m
  const sx = o.off[0] + c.tx + c.s * x, sy = o.off[1] + c.ty + c.s * y
  return sx > M[0] && sx < o.win[0] - M[0] && sy > M[1] && sy < o.win[1] - M[2] ? [sx, sy] : null
}

/* ── kolejność pojawiania się kont: chciwie najdalej od już widocznych, w oknie kamery przy danym p ── */
function order(w: RawWorld): void {
  const o = w.o, rest: Acc[] = [], shown: Acc[] = []
  const cam = (p: number) => camera({ orient: w.orient, off: o.off, pad: o.pad, win: o.win, bb: w.bb, sMin: w.sMin }, p)
  w.accs.forEach((a) => { if (a.t === 'near') { a.rev = -1; shown.push(a) } else rest.push(a) })
  let pPrev = 0
  for (let r = 0; rest.length; r++) {
    let p = Math.max(pOfN(4 + r), pPrev), best: Acc | null = null, bestD = -1, alt: Acc | null = null, altD = -1
    const k = o.nodeR / 18
    for (;;) {                                         // gdy żadne konto nie spełnia odstępu, pojawienie przesuwa się o 0,005 p
      best = null; bestD = -1; alt = null; altD = -1
      const need = (p < LOD[1] ? 46 : p < LOD[2] ? 22 : 0) * k
      const mg: [number, number, number] = p < LOD[1] ? (w.orient === 'v' ? [56, 24, 50] : [30, 30, 60]) : p < LOD[2] ? [18, 18, 18] : [6, 6, 6]
      for (const a of rest) {
        if (a.t === 'inner' && p < LOD[0] + 0.02) continue  // linie między kontami D dopiero po zniknięciu podpisów przystanków
        const sp = inWin(w, p, a.nx, a.ny, mg)
        if (!sp) continue
        let d = 1e9
        for (const b of shown) { const q = inWin(w, p, b.nx, b.ny, -9999) as [number, number]; d = Math.min(d, Math.hypot(q[0] - sp[0], q[1] - sp[1])) }
        if (d >= need && d > bestD) { bestD = d; best = a }
        if (d > altD) { altD = d; alt = a }
      }
      if (best || p >= 1) break
      p = Math.min(1, p + 0.005)
    }
    if (!best && alt) { best = alt; bestD = altD }
    if (!best) {                                       // nic nie mieści się w oknie: najbliższe środka ekranu
      for (const a of rest) {
        const c = cam(p), sx = o.off[0] + c.tx + c.s * a.nx, sy = o.off[1] + c.ty + c.s * a.ny
        const d = -Math.hypot(sx - o.win[0] / 2, sy - o.win[1] / 2)
        if (best === null || d > bestD) { bestD = d; best = a }
      }
    }
    const chosen = best as Acc
    pPrev = p; chosen.rev = p; chosen.nick = p < LOD[1] && bestD >= 84 * o.nodeR / 18
    shown.push(chosen); rest.splice(rest.indexOf(chosen), 1)
  }
}

const r4 = (v: number) => Math.round(v * 10000) / 10000

/** Świat jednej orientacji jako czyste dane (to samo, co zapisuje generator `landingTree.data.ts`). */
export function buildWorld(orient: Orient): World {
  const o = CFG[orient], accs = accounts()
  const gs = accs.map((a) => { const g = geom(a, o), q = o.xy(g.nodeB, g.node); a.nx = q[0]; a.ny = q[1]; return g })
  let x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9
  const ext = (b: number, a: number) => {
    const q = o.xy(b, a)
    x0 = Math.min(x0, q[0]); x1 = Math.max(x1, q[0]); y0 = Math.min(y0, q[1]); y1 = Math.max(y1, q[1])
  }
  gs.forEach((g) => { ext(g.b, g.head); ext(g.tail[2], g.tail[3]); ext(g.tail[0], g.tail[1]); g.stops.forEach((s) => ext(s[0], s[1])) })
  ext(0, 0)                                            // górna / lewa krawędź pigułki utworu
  const bb: [number, number, number, number] = [x0 - 8, y0 - 8, x1 + 8, y1 + 8]
  const sMin = Math.min((o.win[0] - 2 * o.pad[0]) / (bb[2] - bb[0]), (o.win[1] - 2 * o.pad[1]) / (bb[3] - bb[1]))
  const raw: RawWorld = { o, orient, accs, bb, sMin }
  order(raw)                                           // kolejność osobno dla każdej orientacji (inne okno, inne kolizje)

  let nick = 0
  const list: TreeAccount[] = accs.map((a, i) => {
    const g = gs[i], near = a.t === 'near'
    let line = '', head = ''
    if (near) {                                        // głowa (widoczna przy p = 0, rysowana ruchem wejścia) + ogon osobno
      head = pathD(g.segs.slice(0, -1), o)
      line = pathD([['M', g.tail[0], g.tail[1]], g.segs[g.segs.length - 1]], o)
    } else line = pathD(g.segs, o)
    const by = (st: string) => g.stops.filter((s) => s[2] === st)
    // nick tylko dla węzłów DOM (konto przed progiem z3), w kolejności kont, z listy NICKS po kolei
    const named = !near && a.rev < LOD[2] && a.nick ? NICKS[nick++ % NICKS.length] : ''
    return {
      i, c: (i % 3 + 1) as 1 | 2 | 3, d: near, pf: a.pf as PlatformKey, kadr: a.kadr, rev: near ? -1 : r4(a.rev), nick: named,
      x: r1(a.nx), y: r1(a.ny), line, head,
      bg: dots(g.stops, o), ok: dots(by('ok'), o), sch: dots(by('sch'), o),
      wt: by('wt').map((s) => { const q = o.xy(s[0], s[1]); return [r1(q[0]), r1(q[1])] as [number, number] }),
      wn: dots([[g.nodeB, g.node]], o),
    }
  })
  const r3 = (v: number) => Math.round(v * 1000) / 1000
  return {
    orient, win: [...o.win], off: [...o.off], pad: [...o.pad], nodeR: o.nodeR,
    bb: bb.map(r3) as [number, number, number, number], sMin: Math.round(sMin * 1e6) / 1e6,
    // „dziś”: odcinek z D (przy p = 0 dokładnie ten sam) + przedłużenia przez cały świat, widoczne od LOD 1
    today: orient === 'v' ? 'M0 236H358' : 'M592 40V500',
    todayX: orient === 'v' ? 'M' + r1(bb[0]) + ' 236H0M358 236H' + r1(bb[2]) : 'M592 ' + r1(bb[1]) + 'V40M592 500V' + r1(bb[3]),
    trunk: orient === 'v' ? 'M179 44V62' : 'M232 268H264',
    accounts: list,
  }
}
